import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from "react-router-dom";
import { useMediaQuery, TableContainer, Table, TableRow, TableBody, TableCell, TableHead, Grid2, Box, Typography, Button, Dialog, MenuItem, TextField, Switch, Radio, Select, TablePagination, DialogTitle, DialogActions } from '@mui/material';
import { toast, ToastContainer, Slide } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import "./toast.css"

const columns = [
  { id: 'radioButtons', minWidth: 10},
  { id: 'userType', label: 'User Type', minWidth: 130},
  { id: 'organization', label: 'Organization', minWidth: 175},
  { id: 'regEmail', label: 'Registered Email', minWidth: 175},
  { id: 'emailVerify', label: 'Email Verified', minWidth: 100},
  { id: 'dataSubs', label: 'Data Submissions', minWidth: 110},
  { id: 'apiSubs', label: 'API Submissions', minWidth: 223},
  { id: 'lastAccessed', label: 'Last Accessed', minWidth: 50}
];


const UserAdmin = ({isAdmin, isInternal}) => {
  const itemsPerPage = 5;
  const serverHost = process.env.REACT_APP_BACKEND_SERVER_HOST || 'https://localhost:3001';
  const [users, setUsers] = useState([]);
  const [selectedUserForAction, setSelectedUserForAction] = useState({});
  const [selectedValue, setSelectedValue] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(itemsPerPage);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showConfirmDialog, setConfirmDialog] = useState(false);
  const [updateTimestamp, setUpdateTimestamp] = useState(Date.now());
  const [newUser, setNewUser] = useState({
    email: '',
    company: '',
    role: '',
    tracks: []
  });
  const toastOptions = {
    position: "bottom-center",
    autoClose: 8000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "colored",
    transition: Slide
  }

  const fullscreen = useMediaQuery('(max-width:600px)');

  const handleEditDialogOpen = () => setShowEditDialog(true);
  const handleEditDialogClose = () => setShowEditDialog(false);
  const handleAddDialogOpen = () => setShowAddDialog(true);
  const handleAddDialogClose = () => setShowAddDialog(false);
  const handleConfirmDialogOpen = () => setConfirmDialog(true);
  const handleConfirmDialogClose = () => setConfirmDialog(false);

  const currentPageData = users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  }

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0)
  }
  
  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${serverHost}/users`, { withCredentials: true });
      if (response.status === 200) {
        setUsers(response.data);
      }
    } catch (error) {
      toast.error('Error fetching users:', error.response ? error.response.data : error.message);
    }
  }, [serverHost]);

  useEffect(() => {
    fetchUsers();
  }, [serverHost, updateTimestamp, fetchUsers]);

  const handleNewUserChange = (e) => {
    const {name, checked} = e.target;

    setNewUser((prevState) => {
      if(name === 'tracks') {
        let updatedTracks = [...prevState.tracks];

        if(checked) {
          if(!updatedTracks.includes(e.target.value)) {
            updatedTracks.push(e.target.value);
          }
        } else {
          updatedTracks = updatedTracks.filter(track => track !== e.target.value);
        }

        return {
          ...prevState,
          tracks: updatedTracks,
        }
      } else {
        return{
          ...prevState,
          [name]: e.target.value,
        }
      }
    });
  };

  const handleSubmitNewUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${serverHost}/users`, newUser, { withCredentials: true });
      if (response.status === 201) {
        handleAddDialogClose();
        toast.success("User created successfully", toastOptions);
        setUpdateTimestamp(Date.now()); // force reload of users
      }
      else {
        throw new Error(`Failed to create user status: ${response.status}`);
      }
    } catch (error) {
      const msg = `Failed to create user: ${error.response ? error.response.data : error.message}`;
      console.error(msg);
      toast.error(msg, toastOptions);
    }
  };

  const controlProps = (user) => ({
    checked: selectedValue === user.email,
    onChange: (e => handleSelectedUserForActionChange(e, user)),
    value: user.email,
    name: 'radio-button-item'
  });

  const handleSelectedUserForActionChange = (e, user) => {
    setSelectedValue(e.target.value);
    setSelectedUserForAction({user});
  }

  const handleEditUserForAction = (e) => {

    const {name, checked} = e.target;

    setSelectedUserForAction((prevState) => {
      if(name === 'tracks') {
        let updatedTracks = [...(prevState.user.tracks ?? [])];

        if(checked) {
          if(!updatedTracks.includes(e.target.value)) {
            updatedTracks.push(e.target.value);
          }
        } else {
          updatedTracks = updatedTracks.filter(track => track !== e.target.value);
        }

        return {
          ...prevState,
          user: {
            ...prevState.user,
            tracks: updatedTracks,
          }
        }
      } else {
        return{
          ...prevState,
          user: {
            ...prevState.user,
            [name]: e.target.value,
          }
        }
      }
    });
  };
  
  const handleSubmitVerifyEmail = async (e) => {
    
    try {
      const response = await axios.post(`${serverHost}/users/${selectedUserForAction.user.email}/verify`, {
      }, { withCredentials: true });

      if (response.status === 204) {
        toast.success('Verification request email sent successfully', toastOptions);
        setUpdateTimestamp(Date.now()); // force reload of users
      } else {
        console.error(`Failed to verify email error: ${response.status} ${response.statusText}`);
        toast.error(`Failed to verify email error: ${response.status} ${response.statusText}`, toastOptions);
      }
    } catch (error) {
      console.error('Error verifying email:', error);
      toast.error(`Failed to verify email error: ${error.response ? error.response.data : error.message}`, toastOptions);
    }
  };

  const handleSubmitDeleteVerifyEmail = async (e) => {
    
    try {
      const response = await axios.post(`${serverHost}/users/${selectedUserForAction.user.email}/deleteVerify`, {
      }, { withCredentials: true });

      if (response.status === 204) {
        toast.success('Verification delete request processed successfully', toastOptions);
        setUpdateTimestamp(Date.now()); // force reload of users
      } else {
        console.error(`Failed to delete email verification error: ${response.status} ${response.statusText}`);
        toast.error(`Failed to delete email verification error: ${response.status} ${response.statusText}`, toastOptions);
      }
    } catch (error) {
      console.error('Error deleting email verification:', error);
      toast.error(`Failed to delete email verification error: ${error.response ? error.response.data : error.message}`, toastOptions);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.patch(`${serverHost}/users/${selectedUserForAction.user.email}`,
        {
          company: selectedUserForAction.user.company,
          role: selectedUserForAction.user.role,
          tracks: selectedUserForAction.user.tracks
        }, { withCredentials: true });
      if (response.status === 204) {
        toast.success('User updated successfully', toastOptions);
        handleEditDialogClose();
        setUpdateTimestamp(Date.now()); // force reload of users
      }
    } catch (error) {
      console.error('Error updating user:', error.response ? error.response.data : error.message);
      toast.error('Failed to update user', toastOptions);
    }
  };

  const handleDeleteUser = async () => {
    try {
      const response = await axios.delete(`${serverHost}/users/${selectedUserForAction.user.email}`, { withCredentials: true });

      if (response.status === 204) {
        toast.success('User deleted succesfully', toastOptions);
        setUpdateTimestamp(Date.now()); // force reload of users
      } else {
        toast.error('Failed to delete user', toastOptions);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user', toastOptions);
    }
    
    handleConfirmDialogClose();
  };

  const mapVerifiedToUserFriendly = verified => verified ? 'Y' : 'N';

  const changeTrack = {
    'track1': 'Document Validation',
    'track2': 'Match to ID',
    'track3': 'Passive PAD',
    'matching-system': 'Matching System'
  }

  const apiEnabled = (tracks) => {
    const apiList = (tracks ?? []);

    if(apiList !== undefined) {
      const apiModified = apiList.map(api => {
        return changeTrack[api] || api;
      });

      return apiModified.join(", ");
    }

    return apiList.join(", ");
  }

  return (
    <Box className='container-box'>
      <Box className='page-box' >
          <Typography color="fontTheme" 
                      className='page-header'>MyConsole</Typography>
          <Typography className='page-description'>
            Select the <b>ADD USER</b> button and follow the prompts to complete the add user workflow. 
            To edit an existing user account, select the radio button, then proceed with selecting any on the available menu options. 
            Automated system notifications will be sent to the registered email address associated with the user account.
          </Typography>
      </Box>
      <Box className='below-content'>
        <Grid2 container spacing={2} sx={{justifyContent: 'space-between'}}>
            <Grid2 item sm={12} md={6} lg={4} >
                <Typography color="fontTheme"
                            className='page-header'>
                    User Summary
                </Typography>
            </Grid2>
            <Grid2 item sm={12} md={6} lg={8} className='useradmin-buttons'>
                <Box className='table-buttons-div'>
                    <Button className='table-buttons' disabled={isInternal} onClick={() => handleAddDialogOpen()} variant="outlined">ADD USER</Button>
                </Box>
                <Box className='table-buttons-div'>
                    <Button className='table-buttons' onClick={() => handleEditDialogOpen()} disabled={!selectedValue || isInternal} variant="outlined">EDIT USER</Button>
                </Box>
                <Box className='table-buttons-div'>
                    <Button className='table-buttons' component={Link} onClick={() => handleSubmitVerifyEmail()} disabled={!selectedValue || isInternal} variant="outlined">VERIFY EMAIL</Button>
                </Box>
                <Box className='table-buttons-div'>
                    <Button className='table-buttons' component={Link} onClick={() => handleSubmitDeleteVerifyEmail()} disabled={!selectedValue || isInternal} variant="outlined">REMOVE EMAIL VERIFICATION</Button>
                </Box>
                <Box className='table-buttons-div'>
                    <Button className='table-buttons' component={Link} onClick={() => handleConfirmDialogOpen()} disabled={!selectedValue || isInternal} variant="outlined">DELETE USER</Button>
                </Box>
            </Grid2>
        </Grid2>
        <TableContainer>
          <Table sx={{width: '100%', border: 1, borderColor: '#E0E0E0'}}>
              <TableHead sx={{backgroundColor:'#F2F0F0'}}>
                <TableRow sx={{'.MuiTableCell-root': {fontWeight: 'bold'}}}>
                  {columns.map((column) => (
                      <TableCell 
                        key={column.id} 
                        style={{minWidth: column.minWidth, paddingTop: '16px', paddingBottom: '16px'}}>
                          {column.label} 
                      </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
              {currentPageData.length > 0
                && currentPageData.sort((a, b) => a.email.localeCompare(b.email)).map((user, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{width: '0px'}}>
                      <Radio {...controlProps(user)} color='fontTheme' sx={{padding: '13px', marginLeft: '13px', marginRight: '25px'}}/>
                    </TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell>{user.company}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.verified ? mapVerifiedToUserFriendly(user.verified) : ''}</TableCell>
                    <TableCell>{user.tracks ? user.tracks?.includes('dataset') ? 'Enabled' : 'Disabled' : ''}</TableCell>
                    <TableCell sx={{wordWrap: 'break-word', maxWidth: '223px'}}>{user.tracks ? apiEnabled(user.tracks) : ''}</TableCell>
                    <TableCell>{user.last_login_date ? new Date(user.last_login_date).toLocaleString() : ''}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div"
                         count={users.length}
                         page={page}
                         onPageChange={handlePageChange}
                         rowsPerPage={rowsPerPage}
                         onRowsPerPageChange={handleRowsPerPageChange}
                         rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Box>
      
      {/* EDIT DIALOG */}
      <Dialog open={showEditDialog} 
              onClose={handleEditDialogClose}
              fullScreen={fullscreen}
              PaperProps={{
                component: 'form',
                onSubmit: {handleEditUser}
              }}
      >
        <Box className="useradmin-container">
          <Typography className="useradmin-header" >
            EDIT USER
          </Typography>
          <Typography className="useradmin-sub-header" sx={{marginBottom: '10px', marginTop: '20px'}}>
            Registered Email
          </Typography>
          <Typography color='#283B42' className="useradmin-email">
            {selectedUserForAction?.user?.email || 'email@example.com'}
          </Typography>
          <Typography color='#283B42' className="useradmin-sub-header" sx={{marginTop: '20px', marginBottom: '10px'}}>
            User Type
          </Typography>
          <Select onChange={handleEditUserForAction}
                  name="role"
                  defaultValue={selectedUserForAction?.user?.role}
                  className="useradmin-select"
          >
            <MenuItem value="admin">Admin</MenuItem>      
            <MenuItem value="vendor">Vendor</MenuItem>
            <MenuItem value="internal">Internal</MenuItem>
          </Select>
          <Typography className="useradmin-sub-header" sx={{marginTop: '21px'}}>
            Organization
          </Typography>
          <TextField variant='outlined'
                     name="company" 
                     defaultValue={selectedUserForAction?.user?.company} 
                     onChange={handleEditUserForAction} 
                     className="useradmin-textarea"
          />
          <Typography className="useradmin-sub-header" sx={{marginTop: '50px'}}>
            Enabled Submissions
          </Typography>
          <Box display='flex' className="useradmin-api-containers">
            <Typography color='#283B42' className="useradmin-api-headers">
              Dataset Submissions
            </Typography>
            <Switch onChange={handleEditUserForAction}
                    name="tracks"
                    value="dataset"
                    checked={selectedUserForAction?.user?.tracks?.includes('dataset')}
                    sx={{marginRight: '28px'}}
            />
          </Box>
          <Box display='flex' className="useradmin-api-containers">
            <Typography color='#283B42' className="useradmin-api-headers">
              Document Validation API - Docker Submissions
            </Typography>
            <Switch onChange={handleEditUserForAction}
                    name="tracks"
                    value="track1"
                    checked={selectedUserForAction?.user?.tracks?.includes('track1')}
                    sx={{marginRight: '28px'}}
            />
          </Box>
          <Box display='flex' className="useradmin-api-containers">
            <Typography color='#283B42' className="useradmin-api-headers">
              Match to ID API - Docker Submissions
            </Typography>
            <Switch onChange={handleEditUserForAction}
                    name="tracks"
                    value="track2"
                    checked={selectedUserForAction?.user?.tracks?.includes('track2')}
                    sx={{marginRight: '28px'}}
            />
          </Box>
          <Box display='flex' className="useradmin-api-containers">
            <Typography color='#283B42' className="useradmin-api-headers">
              Matching System API - Docker Submissions
            </Typography>
            <Switch onChange={handleEditUserForAction}
                    name="tracks"
                    value="matching-system"
                    checked={selectedUserForAction?.user?.tracks?.includes('matching-system')}
                    sx={{marginRight: '28px'}}
            />
          </Box>
          <Box display='flex' className="useradmin-api-containers">
            <Typography color='#283B42' className="useradmin-api-headers">
              Passive PAD API - Docker Submissions
            </Typography>
            <Switch onChange={handleEditUserForAction}
                    name="tracks"
                    value="track3"
                    checked={selectedUserForAction?.user?.tracks?.includes('track3')}
                    sx={{marginRight: '28px'}}
            />
          </Box>
          <Box className="useradmin-buttons-container">
            <Button type="submit" 
                    onClick={handleEditUser} 
                    variant="oulined"
                    sx={{width: '210px', marginRight: '40px'}}
                    className="action-buttons"
            >
              SAVE EDITS
            </Button>
            <Button onClick={handleEditDialogClose}
                    className="action-buttons"
                    sx={{width: '210px'}} 
                    variant="outlined"
            >
              CANCEL
            </Button>
          </Box>
        </Box> 
      </Dialog>

      {/* ADD DIALOG */}
      <Dialog open={showAddDialog} 
              onClose={handleAddDialogClose}
              fullScreen={fullscreen}
              PaperProps={{
                component: 'form',
                onSubmit: {handleSubmitNewUser}
              }}
      >
        <Box className="useradmin-container">
          <Typography className="useradmin-header" >
            ADD USER
          </Typography>
          <Typography className="useradmin-sub-header" sx={{ marginBottom: '10px', marginTop: '20px'}}>
            Email
          </Typography>
          <TextField variant='outlined' 
                     name="email"
                     onChange={handleNewUserChange} 
                     className="useradmin-textarea"
          />
          <Typography className="useradmin-sub-header" sx={{marginTop: '30px', marginBottom: '10px'}}>
            Organization
          </Typography>
          <TextField variant='outlined' 
                     name="company" 
                     onChange={handleNewUserChange} 
                     className="useradmin-textarea"
          />
          <Typography className="useradmin-sub-header" sx={{ marginTop: '30px', marginBottom: '10px'}}>
            User Type
          </Typography>
          <Select name="role"
                  onChange={handleNewUserChange}
                  className="useradmin-select"
          >
            <MenuItem value="admin">Admin</MenuItem>      
            <MenuItem value="vendor">Vendor</MenuItem>
            <MenuItem value="internal">Internal</MenuItem>  
          </Select>
          
          <Typography className="useradmin-sub-header" sx={{marginTop: '28px'}}>
            Enabled Submissions
          </Typography>
          <Box display='flex' className="useradmin-api-containers">
            <Typography color='#283B42' className="useradmin-api-headers">
              Dataset Submissions
            </Typography>
            <Switch onChange={handleNewUserChange}
                    name="tracks"
                    value="dataset"
                    checked={newUser.tracks.includes('dataset')}
                    sx={{marginRight: '28px'}}
            />
          </Box>
          <Box display='flex' className="useradmin-api-containers">
            <Typography color='#283B42' className="useradmin-api-headers">
              Document Validation API - Docker Submissions
            </Typography>
            <Switch onChange={handleNewUserChange}
                    name="tracks"
                    value="track1"
                    checked={newUser.tracks.includes('track1')}
                    sx={{marginRight: '28px'}}
            />
          </Box>
          <Box display='flex' className="useradmin-api-containers">
            <Typography color='#283B42' className="useradmin-api-headers">
              Match to ID API - Docker Submissions
            </Typography>
            <Switch onChange={handleNewUserChange}
                    name="tracks"
                    value="track2"
                    checked={newUser.tracks.includes('track2')}
                    sx={{marginRight: '28px'}}
            />
          </Box>
          <Box display='flex' className="useradmin-api-containers">
            <Typography color='#283B42' className="useradmin-api-headers">
              Matching System API - Docker Submissions
            </Typography>
            <Switch onChange={handleNewUserChange}
                    name="tracks"
                    value="matching-system"
                    checked={newUser.tracks.includes('matching-system')}
                    sx={{marginRight: '28px'}}
            />
          </Box>
          <Box display='flex' className="useradmin-api-containers">
            <Typography color='#283B42' className="useradmin-api-headers">
              Passive PAD API - Docker Submissions
            </Typography>
            <Switch onChange={handleNewUserChange}
                    name="tracks"
                    value="track3"
                    checked={newUser.tracks.includes('track3')}
                    sx={{marginRight: '28px'}}
            />
          </Box>
          <Box className="useradmin-buttons-container">
            <Button type="submit" 
                    onClick={handleSubmitNewUser} 
                    variant="outlined"
                    sx={{width: '210px', marginRight: '40px'}}
                    className="action-buttons"
            >
              ADD USER
            </Button>
            <Button onClick={handleAddDialogClose}
                    className="action-buttons"
                    sx={{width: '210px'}} 
                    variant="outlined"
            >
              CANCEL
            </Button>
          </Box>
        </Box> 
      </Dialog>

    {/* CONFIRM DELETE USER DIALOG */}
    <Dialog
      open={showConfirmDialog}
      onClose={handleConfirmDialogClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      <DialogTitle>
        Are you sure you want to delete user {selectedUserForAction?.user?.email}?
      </DialogTitle>
      <DialogActions>
        <Button onClick={handleDeleteUser}>YES</Button>
        <Button onClick={handleConfirmDialogClose}>NO</Button>
      </DialogActions>
    </Dialog>

    <ToastContainer />
    </Box>
  );
  
}

export default UserAdmin;
